import React from 'react';
import SafeIcon from '../common/SafeIcon';
import RichTextField from '../common/RichTextField';
import { SESSION_LEVELS, SUGGESTED_TRAINING_TAGS } from '../../constants/training';

function FieldLabel({ children, required }) {
  return (
    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
      {children}
      {required && <span className="text-brand-cyan ml-1">*</span>}
    </label>
  );
}

function TextInput({ className = '', ...props }) {
  return (
    <input
      className={`w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-all ${className}`}
      {...props}
    />
  );
}

export default function SessionMetadataEditorForm({
  title,
  summary,
  transcript,
  speakerName,
  tags,
  tagInput,
  level,
  tagSuggestions = SUGGESTED_TRAINING_TAGS,
  disabled = false,
  onTitleChange,
  onSummaryChange,
  onTranscriptChange,
  onSpeakerNameChange,
  onTagsChange,
  onTagInputChange,
  onLevelChange,
}) {
  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (!trimmed || tags.includes(trimmed)) return;
    onTagsChange([...tags, trimmed]);
  };

  const removeTag = (tag) => {
    onTagsChange(tags.filter((t) => t !== tag));
  };

  return (
    <div className="space-y-6">
      <div>
        <FieldLabel required>Título</FieldLabel>
        <TextInput value={title} onChange={(e) => onTitleChange(e.target.value)} disabled={disabled} required />
      </div>

      <div>
        <FieldLabel required>Resumen</FieldLabel>
        <RichTextField
          value={summary}
          onChange={onSummaryChange}
          disabled={disabled}
          minHeight="140px"
        />
      </div>

      {onTranscriptChange && (
        <div>
          <FieldLabel>Transcripción</FieldLabel>
          <RichTextField
            value={transcript ?? ''}
            onChange={onTranscriptChange}
            disabled={disabled}
            placeholder="Opcional — pega la transcripción si la tienes"
            minHeight="200px"
          />
        </div>
      )}

      <div>
        <FieldLabel>Instructor / Ponente</FieldLabel>
        <TextInput
          value={speakerName}
          onChange={(e) => onSpeakerNameChange(e.target.value)}
          disabled={disabled}
          placeholder="Nombre visible en la biblioteca"
        />
      </div>

      <div className="space-y-3">
        <FieldLabel>Etiquetas</FieldLabel>
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full gfi-gradient-bg text-brand-navy text-[10px] font-black uppercase tracking-wider"
            >
              {tag}
              <button type="button" onClick={() => removeTag(tag)} disabled={disabled}>
                <SafeIcon name="X" className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <TextInput
            value={tagInput}
            onChange={(e) => onTagInputChange(e.target.value)}
            disabled={disabled}
            placeholder="Escribe y pulsa Enter"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag(tagInput);
                onTagInputChange('');
              }
            }}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => { addTag(tagInput); onTagInputChange(''); }}
            className="px-4 rounded-xl border border-[var(--border-subtle)] text-[10px] font-black uppercase tracking-widest"
          >
            Añadir
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {tagSuggestions.filter((t) => !tags.includes(t)).map((tag) => (
            <button
              key={tag}
              type="button"
              disabled={disabled}
              onClick={() => addTag(tag)}
              className="px-3 py-1 rounded-full border border-[var(--border-subtle)] text-[10px] font-bold text-[var(--text-secondary)] hover:border-brand-cyan hover:text-[var(--text-main)]"
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      <div>
        <FieldLabel required>Nivel</FieldLabel>
        <select
          value={level}
          onChange={(e) => onLevelChange(e.target.value)}
          disabled={disabled}
          className="w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-green"
        >
          {SESSION_LEVELS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
